import React, { useRef, useState } from 'react';
import LazyTable from 'app/component/lazy-table';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { boldNameFormatter } from 'app/shared/util/formatters';
import { FilterMatchMode } from 'primereact/api';
import DeleteClientDialog from 'app/main/client/delete-client-dialog';

const ClientTable: React.FC = () => {
  const account = useAppSelector(state => state.authentication.account);
  const isAdminOrAssistant = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.ASSISTANT]);
  const isAdmin = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN]);

  const tableRef = useRef<any>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<any>(null);

  const sendEmail = email => {
    const mailtoLink = `mailto:${encodeURIComponent(email)}`;

    window.location.href = mailtoLink;
  };

  const onPhoneCall = phoneNr => {
    const telLink = `tel:${encodeURIComponent(phoneNr)}`;

    window.location.href = telLink;
  };

  const phoneFormatter = value => {
    if (value) {
      return (
        <span style={{ cursor: 'pointer' }} onClick={() => onPhoneCall(value)}>
          {value}
        </span>
      );
    }
  };

  const emailFormatter = value => {
    if (value) {
      return (
        <span style={{ cursor: 'pointer' }} onClick={() => sendEmail(value)}>
          {value}
        </span>
      );
    }
  };

  const openDeleteDialog = rowData => {
    setClientToDelete(rowData);
    setDeleteDialogVisible(true);
  };

  const onDeleted = () => {
    setDeleteDialogVisible(false);
    setClientToDelete(null);
    tableRef.current?.reloadData?.();
  };

  const additionalActions = isAdmin
    ? [
        {
          icon: 'pi pi-trash',
          tooltip: 'Vymazať',
          severity: 'danger',
          onClick: openDeleteDialog,
        },
      ]
    : undefined;

  const table = LazyTable({
    ref: tableRef,
    initialSortField: 'lastName',
    createUrl: '/create-client',
    labelForCreateEntity: isAdminOrAssistant ? 'Vytvoriť klienta' : null,
    serverUrl: '/api/clients/search',
    editActionAllowed: isAdminOrAssistant,
    infoActionAllowed: true,
    exportUrl: '/api/clients/export',
    globalFilterFields: ['businessName', 'firstName', 'lastName', 'email'],
    additionalActions,
    columns: [
      { field: 'id', header: 'ID', filter: true, sortable: true },
      { field: 'nameFullFormatted', filter: true, header: 'Meno klienta', sortable: false, formatter: (e, v) => boldNameFormatter(e) },
      { field: 'email', header: 'Email', filter: true, sortable: true, formatter: emailFormatter },
      { field: 'phone', header: 'Telefón', filter: true, sortable: true, formatter: phoneFormatter },
      { field: 'ico', header: 'IČO', filter: true, sortable: true },
      { field: 'birthNr', header: 'Rodné číslo', filter: true, sortable: true },
      { field: 'agentName', header: 'Meno posledného poradcu', sortable: false, formatter: (e, v) => boldNameFormatter(e) },
    ],
    filters: {
      id: { value: null, matchMode: FilterMatchMode.EQUALS },
      nameFullFormatted: { value: null, matchMode: FilterMatchMode.CONTAINS },
      email: { value: null, matchMode: FilterMatchMode.CONTAINS },
      phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
      ico: { value: null, matchMode: FilterMatchMode.CONTAINS },
      birthNr: { value: null, matchMode: FilterMatchMode.CONTAINS },
      agentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  return (
    <>
      {table}
      <DeleteClientDialog
        client={clientToDelete}
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onDeleted={onDeleted}
      />
    </>
  );
};

export default ClientTable;
